<template>
    <Head :title="'Buy a Home in St. Johns County'" >
        <link rel="canonical" href="https://www.ginabetbeze.com/buying" />
        <meta name="description" content="Buy a home in St. Johns County with Gina Betbeze. Expert guidance for Julington Creek and beyond, from search to closing, tailored to your needs." />
    </Head>
    <!-- Simple Blurb -->

    <PageContent
        :id="'buying-page'"
        :breadcrumbs="['Home','Buying']"
        :header="'Buyer Form'"
    >
        <BasicContainer>
            <FormContainer>
                <form @submit.prevent="submitVelocityForm">

                    <VelocityFormAnimation ref="formAnimation"
                                           :paperTitle="'Buyer Form'">
                        <div>
                            <FieldGroup>
                                <div  class="w-full text-center">
                                    <h2 id="contact-head" class="text-3xl font-subtitle">
                                        Enter your details, and let's connect to find your dream house.
                                    </h2>
                                </div>
                            </FieldGroup>
                            <FieldGroup>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="buyingForm.firstname"
                                        :name="`firstname`"
                                        :label="'First Name'"
                                        :type="`text`"
                                        required
                                    />
                                </div>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="buyingForm.lastname"
                                        :name="`lastname`"
                                        :label="'Last Name'"
                                        :type="`text`"
                                        required
                                    />
                                </div>
                            </FieldGroup>

                            <FieldGroup>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="buyingForm.email"
                                        :name="`email`"
                                        :label="'Email'"
                                        :type="`email`"
                                        required
                                    />
                                </div>
                                <div class="w-full tablet-sm:w-1/2">
                                    <Input
                                        v-model="buyingForm.phone"
                                        :name="`phone`"
                                        :label="'Phone (optional)'"
                                        :type="`tel`"
                                    />
                                </div>
                            </FieldGroup>

                            <FieldGroup>
                                <div class="w-full">
                                    <Radio
                                        v-model="buyingForm.priceBreakpoint"
                                        type="text"
                                        label="Price Range"
                                        required
                                        :label="'Price Range'"
                                        :options="[
                                            { value: '100-300k', label: '$100,000-$300,000' },
                                            { value: '300-500k', label: '$300,000-$500,000' },
                                            { value: '500k+', label: '$500,000+' },
                                        ]"
                                        :name="`priceBreakpoint`"
                                    />
                                </div>
                            </FieldGroup>
                            <FieldGroup>
                                <div class="w-full">
                                    <Radio
                                        v-model="buyingForm.timeFrame"
                                        type="text"
                                        label="Time Frame"
                                        required
                                        :label="'Price Range'"
                                        :options="[
                                            { value: '0-3months', label: '0 - 3 Months' },
                                            { value: '3-6months', label: '3 - 6 Months' },
                                            { value: '6-12months', label: '12+ Months' },
                                        ]"
                                        :name="`timeFrame`"
                                    />
                                </div>
                            </FieldGroup>
                            <div class="w-full flex justify-center">
                                <Button class="max-w-96">Submit</Button>
                            </div>
                        </div>
                    </VelocityFormAnimation>
                </form>
            </FormContainer>
        </BasicContainer>
    </PageContent>
</template>
<script setup>
import { Inertia } from '@inertiajs/inertia'
import Input from "../Components/Forms/Input.vue";
import Radio from "../Components/Forms/Radio.vue";
import Button from "../Components/Forms/Button.vue";
import Breadcrumbs from "@/Components/PageContent/Breadcrumbs.vue";
import PageContent from "../Components/Layout/PageContent.vue";
import HomeLayout from "../Layouts/HomeLayout.vue";
import MainHead from "@/Components/PageContent/MainHead.vue";
import BasicContainer from "@/Components/PageContent/Containers/BasicContainer.vue";
import VelocityFormAnimation from "@/Components/Forms/VelocityFormAnimation.vue";

import { Head, Link, useForm, usePage } from "@inertiajs/vue3";
import FormContainer from "@/Components/PageContent/Containers/FormContainer.vue";
import FieldGroup from "@/Components/Forms/FieldGroup.vue";
import { route } from "ziggy-js";
import { getCurrentInstance, ref } from "vue";

defineOptions({ layout: HomeLayout });
const page = usePage();
const props = defineProps({
    title: {
        type: String,
        required: true,
    },
});

const formAnimation = ref(null);
const buyingForm = useForm({
    firstname: "",
    lastname: "",
    phone: "",
    email: "",
    priceBreakpoint: "", // radio button
    timeFrame: "", // dropdown
});
// This is the manual version when you know what form you're using. The one on CallToAction lets you set an option
const submitVelocityForm = async (e) => {
    e.preventDefault();

    let formKey = 'buying';
    let redirectKey = "/buying";
    // Collect the submission data in a json string for insertion into submission table
    const jsonData = JSON.stringify(buyingForm.data());

    const velocitySubmissionForm = useForm({
        form_key: formKey,
        field_values: jsonData,
        redirect: redirectKey,
        recaptcha_token: ""
    });

    try {
        // Execute reCAPTCHA
        velocitySubmissionForm.recaptcha_token  = await grecaptcha.enterprise.execute('6LeDhtAqAAAAAJm-0AUDswv8xmB2K8knAmdzyDHi', { action: 'SUBMIT' });
    } catch (error) {
        console.error('reCAPTCHA error:', error);
    }
    velocitySubmissionForm.post( route('velocitySubmission.submitPublic', formKey ),{
        preserveState: true,
        preserveScroll: true,

        onError: (errors) => {
            // Log the detailed error object to the console for debugging
            console.error('Error Details:', errors);

            // Construct a detailed error message if the errors object contains specific error messages
            let detailedErrorMessage = 'There was an error submitting the form "' + page.props.velocityForm.title +
                '".';
            if (errors && typeof errors === 'object' && Object.keys(errors).length > 0) {
                // Append each error message to the detailed error message
                Object.keys(errors).forEach((key) => {
                    detailedErrorMessage += ` ${key}: ${errors[key]}`;
                });
            }
            formAnimation.value.toggleOnEnvelopeFailAnimation(detailedErrorMessage);

            // Display the detailed error message in the UI
            page.props.messages.push({
                message: detailedErrorMessage,
                duration: 10,
                type: "error"
            });
            console.log(detailedErrorMessage);
        },
        onSuccess: () => {
            formAnimation.value.toggleOnEnvelopeSentAnimation();

            setTimeout(() => {
                buyingForm.reset();
                Inertia.visit(route(route().current()));
            }, 7500)

        },
    });
};


</script>
